import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '@dental-manager/shared';

/**
 * Devise et locale courantes.
 *
 * État au niveau du module — volontairement : les ~40 appels existants à
 * formatCurrency(value) restent inchangés. La devise ne change qu'au login /
 * changement de cabinet (re-render complet), la locale au changement de langue
 * (i18next re-rend les composants qui utilisent t()).
 */
let currentCurrency: string = DEFAULT_CURRENCY;
let currentLocale: string = DEFAULT_LOCALE;

/** Appelé par AuthContext au login, getMe, switchAccount et changement de langue. */
export function setCurrencyContext(currency?: string | null, locale?: string | null): void {
  if (currency) currentCurrency = currency;
  if (locale) currentLocale = locale;
}

export function getCurrency(): string {
  return currentCurrency;
}

/** BCP 47 locale of the UI — for the rare Intl call that needs its own options. */
export function getLocale(): string {
  return currentLocale;
}

/**
 * Symbole de la devise courante (€, RON, CHF…) — pour les libellés du type
 * "Valeur (€)" et les axes de graphiques.
 */
export function currencySymbol(): string {
  const parts = new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
  }).formatToParts(0);
  return parts.find(p => p.type === 'currency')?.value ?? currentCurrency;
}

/**
 * Format a number as currency in the cabinet's currency and the UI locale
 * (e.g., 1 000,00 € en fr-FR, 1.000,00 RON en ro-RO)
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
  }).format(value);
}

/**
 * Format a number as currency without decimals (e.g., 1 000 €) — pour les KPI
 */
export function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: currentCurrency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number with the UI locale thousand separators (e.g., 1 500 → "1 500")
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat(currentLocale).format(value);
}

/**
 * Parse a value into a Date without the UTC-midnight shift.
 *
 * `new Date("2026-08-14")` is parsed as UTC midnight, which serializes back as
 * the 13th in CET/CEST. Les colonnes DATE de PostgreSQL arrivent en string
 * `YYYY-MM-DD` (cf. le type parser dans config/database.ts) — on les découpe
 * pour construire une date locale. Les timestamps ISO complets gardent le
 * parsing natif.
 */
function toDate(value: string | Date | number): Date {
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.slice(0, 10));
  if (dateOnly && value.length <= 10) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  return new Date(value);
}

/** Date in the UI locale (e.g., 14/08/2026 en fr-FR, 14.08.2026 en ro-RO) */
export function formatDate(value: string | Date | number, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(currentLocale, opts).format(toDate(value));
}

/** Time in the UI locale — defaults to hour + minute */
export function formatTime(value: string | Date | number, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(
    currentLocale,
    opts ?? { hour: '2-digit', minute: '2-digit' }
  ).format(toDate(value));
}

/** Date + time in the UI locale */
export function formatDateTime(value: string | Date | number, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(
    currentLocale,
    opts ?? { dateStyle: 'short', timeStyle: 'short' }
  ).format(toDate(value));
}

/**
 * Format product display name with optional variant label
 * @param productName - The product name
 * @param variantLabel - The variant label (null/empty for unique products)
 * @param separator - Separator between name and label (default: " — ")
 * @returns Formatted display string
 *
 * @example
 * formatProductVariant("Gants Latex", "M") // "Gants Latex — M"
 * formatProductVariant("Gants Latex M", null) // "Gants Latex M"
 * formatProductVariant("Gants Latex M", "") // "Gants Latex M"
 */
export function formatProductVariant(
  productName: string,
  variantLabel?: string | null,
  separator: string = ' — '
): string {
  if (!variantLabel || variantLabel.trim() === '') {
    return productName;
  }
  return `${productName}${separator}${variantLabel}`;
}
